import React, { useState, useEffect, useCallback } from 'react';
import { apiClient, uploadFile, API_BASE } from '../api/client';

// Design tokens
const PRIMARY = '#1a6b3c';
const TEXT_DARK = '#0f2d1f';
const TEXT_MUTED = '#6b7280';
const BORDER_LIGHT = '#e5e7eb';
const BG_PAGE = '#f9fafb';
const BG_CARD = '#ffffff';

// Types
interface EventLite {
  id: string;
  name: string;
  date?: string;
}

interface FoodEventLink {
  id: string;
  eventId: string;
  event?: EventLite;
}

interface MenuItem {
  id: string;
  name: string;
  description?: string;
  category?: string;
  imageUrl?: string;
  tags?: string;
  isActive: boolean;
  sortOrder?: number;
  vendorOfferings?: VendorOffering[];
  eventLinks?: FoodEventLink[];
}

interface VendorOffering {
  id: string;
  vendorId: string;
  foodMenuItemId: string;
  price: number;
  available: boolean;
  notes?: string;
  foodMenuItem?: MenuItem;
}

interface Vendor {
  id: string;
  businessName: string;
  category?: string | null;
}

type SubView = 'menu-items' | 'offerings';

const SUB_VIEWS: { key: SubView; label: string }[] = [
  { key: 'menu-items', label: 'Menu Items' },
  { key: 'offerings', label: 'Vendor Offerings' },
];

const CATEGORIES = [
  'Appetizers',
  'Main Dishes',
  'Sides',
  'Desserts',
  'Drinks',
  'Snacks',
  'Traditional',
  'Grilled',
  'Seafood',
  'Vegetarian',
];

// Styles
const styles: Record<string, React.CSSProperties> = {
  page: {
    padding: '2rem',
    backgroundColor: BG_PAGE,
    minHeight: '100vh',
  },
  header: {
    marginBottom: '1.5rem',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: TEXT_DARK,
    margin: '0 0 0.25rem',
  },
  subtitle: {
    color: TEXT_MUTED,
    fontSize: '0.9rem',
    margin: 0,
  },
  subNav: {
    display: 'flex',
    gap: '0.5rem',
    borderBottom: `2px solid ${BORDER_LIGHT}`,
    marginBottom: '1.5rem',
  },
  subTab: {
    padding: '0.6rem 1.2rem',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    fontSize: '0.95rem',
    color: TEXT_MUTED,
    borderBottom: '2px solid transparent',
    marginBottom: '-2px',
    transition: 'color 0.2s, border-color 0.2s',
  },
  subTabActive: {
    padding: '0.6rem 1.2rem',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    fontSize: '0.95rem',
    color: PRIMARY,
    fontWeight: 600,
    borderBottom: `2px solid ${PRIMARY}`,
    marginBottom: '-2px',
  },
  card: {
    backgroundColor: BG_CARD,
    borderRadius: '8px',
    border: `1px solid ${BORDER_LIGHT}`,
    padding: '1.5rem',
    marginBottom: '1rem',
  },
  btnPrimary: {
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: 600,
  },
  btnSecondary: {
    backgroundColor: '#e0e0e0',
    color: '#333',
    border: 'none',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.9rem',
  },
  btnDanger: {
    backgroundColor: '#d32f2f',
    color: '#fff',
    border: 'none',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.9rem',
  },
  iconBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '8px 10px',
    fontSize: '1rem',
    borderRadius: '4px',
    lineHeight: 1,
    transition: 'background-color 0.15s',
    minWidth: '36px',
    minHeight: '36px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
  },
  th: {
    textAlign: 'left' as const,
    padding: '0.6rem 0.75rem',
    borderBottom: `2px solid ${BORDER_LIGHT}`,
    fontSize: '0.8rem',
    fontWeight: 600,
    color: TEXT_MUTED,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.03em',
  },
  td: {
    padding: '0.6rem 0.75rem',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
    fontSize: '0.9rem',
    color: TEXT_DARK,
  },
  formGroup: {
    marginBottom: '0.75rem',
  },
  label: {
    display: 'block',
    fontSize: '0.85rem',
    fontWeight: 600,
    color: TEXT_DARK,
    marginBottom: '0.25rem',
  },
  input: {
    width: '100%',
    padding: '0.5rem 0.75rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    fontSize: '0.9rem',
    boxSizing: 'border-box' as const,
  },
  textarea: {
    width: '100%',
    padding: '0.5rem 0.75rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    fontSize: '0.9rem',
    minHeight: '80px',
    resize: 'vertical' as const,
    boxSizing: 'border-box' as const,
  },
  select: {
    width: '100%',
    padding: '0.5rem 0.75rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    fontSize: '0.9rem',
    boxSizing: 'border-box' as const,
  },
  badge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  badgeActive: {
    backgroundColor: '#dcfce7',
    color: '#166534',
  },
  badgeInactive: {
    backgroundColor: '#fee2e2',
    color: '#991b1b',
  },
  formActions: {
    display: 'flex',
    gap: '0.5rem',
    marginTop: '1rem',
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  error: {
    color: '#d32f2f',
    fontSize: '0.85rem',
    padding: '0.5rem',
    backgroundColor: '#fef2f2',
    borderRadius: '6px',
    marginBottom: '1rem',
  },
  loading: {
    textAlign: 'center' as const,
    padding: '2rem',
    color: TEXT_MUTED,
  },
};

// ============ MENU ITEMS TAB ============
function MenuItemsTab() {
  const [items, setItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState<MenuItem | null>(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formCategory, setFormCategory] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formTags, setFormTags] = useState('');
  const [formImageUrl, setFormImageUrl] = useState('');
  const [formSortOrder, setFormSortOrder] = useState('');
  const [formEventIds, setFormEventIds] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);

  // Events list (for association multi-select)
  const [events, setEvents] = useState<EventLite[]>([]);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await apiClient<MenuItem[]>('/food-catalog/menu-items');
      setItems(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load menu items');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchEvents = useCallback(async () => {
    try {
      const data = await apiClient<EventLite[]>('/events');
      setEvents(Array.isArray(data) ? data : []);
    } catch {
      setEvents([]);
    }
  }, []);

  useEffect(() => {
    fetchItems();
    fetchEvents();
  }, [fetchItems, fetchEvents]);

  const resetForm = () => {
    setFormName('');
    setFormCategory('');
    setFormDescription('');
    setFormTags('');
    setFormImageUrl('');
    setFormSortOrder('');
    setFormEventIds([]);
    setEditingItem(null);
    setShowForm(false);
  };

  const openEditForm = (item: MenuItem) => {
    setEditingItem(item);
    setFormName(item.name);
    setFormCategory(item.category || '');
    setFormDescription(item.description || '');
    setFormTags(item.tags || '');
    setFormImageUrl(item.imageUrl || '');
    setFormSortOrder(item.sortOrder?.toString() || '');
    setFormEventIds((item.eventLinks || []).map((l) => l.eventId));
    setShowForm(true);
  };

  const toggleEventId = (eventId: string) => {
    setFormEventIds((prev) =>
      prev.includes(eventId) ? prev.filter((id) => id !== eventId) : [...prev, eventId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formName.trim()) return;

    const body: any = {
      name: formName.trim(),
      category: formCategory || undefined,
      description: formDescription || undefined,
      tags: formTags || undefined,
      imageUrl: formImageUrl || undefined,
      sortOrder: formSortOrder ? Number(formSortOrder) : undefined,
      eventIds: formEventIds,
    };

    try {
      setError('');
      if (editingItem) {
        await apiClient(`/food-catalog/menu-items/${editingItem.id}`, {
          method: 'PATCH',
          body,
        });
      } else {
        await apiClient('/food-catalog/menu-items', {
          method: 'POST',
          body,
        });
      }
      resetForm();
      await fetchItems();
    } catch (err: any) {
      setError(err.message || 'Failed to save menu item');
    }
  };

  const handleDelete = async (item: MenuItem) => {
    if (!window.confirm(`Delete "${item.name}"? This cannot be undone.`)) return;
    try {
      setError('');
      await apiClient(`/food-catalog/menu-items/${item.id}`, { method: 'DELETE' });
      await fetchItems();
    } catch (err: any) {
      setError(err.message || 'Failed to delete menu item');
    }
  };

  const handleToggleActive = async (item: MenuItem) => {
    try {
      setError('');
      await apiClient(`/food-catalog/menu-items/${item.id}`, {
        method: 'PATCH',
        body: { isActive: !item.isActive },
      });
      await fetchItems();
    } catch (err: any) {
      setError(err.message || 'Failed to update item');
    }
  };

  if (loading) {
    return <div style={styles.loading}>Loading menu items...</div>;
  }

  return (
    <div>
      {error && <div style={styles.error}>{error}</div>}

      <div style={styles.toolbar}>
        <span style={{ color: TEXT_MUTED, fontSize: '0.85rem' }}>
          {items.length} menu item{items.length !== 1 ? 's' : ''}
        </span>
        {!showForm && (
          <button
            style={styles.btnPrimary}
            onClick={() => setShowForm(true)}
            aria-label="Add menu item"
          >
            + Add Menu Item
          </button>
        )}
      </div>

      {showForm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem',
          }}
          onClick={(e) => e.target === e.currentTarget && resetForm()}
          role="dialog"
          aria-modal="true"
          aria-label={editingItem ? 'Edit Menu Item' : 'Add Menu Item'}
        >
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '550px',
            maxHeight: '90vh',
            overflowY: 'auto',
            padding: '1.5rem',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            position: 'relative',
          }}>
            <button
              onClick={resetForm}
              style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                background: 'none',
                border: 'none',
                fontSize: '1.2rem',
                cursor: 'pointer',
                padding: '4px 8px',
                borderRadius: '6px',
                color: TEXT_MUTED,
              }}
              aria-label="Close"
            >
              ✕
            </button>
            <h3 style={{ margin: '0 0 1.25rem', fontSize: '1.1rem', color: TEXT_DARK }}>
              {editingItem ? '✏️ Edit Menu Item' : '➕ Add Menu Item'}
            </h3>
            <form onSubmit={handleSubmit}>
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="menuItemName">Name *</label>
              <input
                id="menuItemName"
                style={styles.input}
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                required
                placeholder="e.g., Jollof Rice"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="menuItemCategory">Category</label>
              <select
                id="menuItemCategory"
                style={styles.select}
                value={formCategory}
                onChange={(e) => setFormCategory(e.target.value)}
              >
                <option value="">— Select category —</option>
                {CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="menuItemDesc">Description</label>
              <textarea
                id="menuItemDesc"
                style={styles.textarea}
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder="Brief description of the dish"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="menuItemTags">Tags (comma-separated)</label>
              <input
                id="menuItemTags"
                style={styles.input}
                value={formTags}
                onChange={(e) => setFormTags(e.target.value)}
                placeholder="e.g., spicy, gluten-free, popular"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Image</label>
              {formImageUrl && (
                <div style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <img
                    src={formImageUrl.startsWith('http') ? formImageUrl : `${window.location.origin.replace('kduhub.com', 'api.kduhub.com').replace(':5173', ':3000')}${formImageUrl}`}
                    alt="Preview"
                    style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '8px', border: `1px solid ${BORDER_LIGHT}` }}
                  />
                  <button
                    type="button"
                    style={{ ...styles.iconBtn, fontSize: '0.8rem' }}
                    onClick={() => setFormImageUrl('')}
                    aria-label="Remove image"
                  >
                    ✕ Remove
                  </button>
                </div>
              )}
              <input
                id="menuItemImageFile"
                type="file"
                accept="image/*"
                disabled={uploading}
                style={{ fontSize: '0.85rem' }}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  if (file.size > 5 * 1024 * 1024) {
                    setError('Image must be under 5MB');
                    return;
                  }
                  try {
                    setUploading(true);
                    setError('');
                    const result = await uploadFile<{ imageUrl?: string; error?: string }>('/vendor-catalog/upload-image', file);
                    if (result.error) {
                      setError(result.error);
                    } else if (result.imageUrl) {
                      setFormImageUrl(result.imageUrl);
                    }
                  } catch (err: any) {
                    setError(err.message || 'Failed to upload image');
                  } finally {
                    setUploading(false);
                    e.target.value = '';
                  }
                }}
              />
              {uploading && <span style={{ fontSize: '0.8rem', color: TEXT_MUTED }}>Uploading...</span>}
              <div style={{ fontSize: '0.75rem', color: TEXT_MUTED, marginTop: '0.25rem' }}>Max 5MB. Will be resized to 400px thumbnail.</div>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="menuItemSort">Sort Order</label>
              <input
                id="menuItemSort"
                style={styles.input}
                type="number"
                value={formSortOrder}
                onChange={(e) => setFormSortOrder(e.target.value)}
                placeholder="0"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Served at Events</label>
              {events.length === 0 ? (
                <div style={{ fontSize: '0.8rem', color: TEXT_MUTED }}>No events available.</div>
              ) : (
                <div style={{ border: `1px solid ${BORDER_LIGHT}`, borderRadius: '6px', maxHeight: '160px', overflowY: 'auto', padding: '0.5rem' }}>
                  {events.map((ev) => (
                    <label
                      key={ev.id}
                      style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.35rem 0.25rem', fontSize: '0.85rem', color: TEXT_DARK, cursor: 'pointer' }}
                    >
                      <input
                        type="checkbox"
                        checked={formEventIds.includes(ev.id)}
                        onChange={() => toggleEventId(ev.id)}
                      />
                      <span>{ev.name}</span>
                    </label>
                  ))}
                </div>
              )}
              <div style={{ fontSize: '0.75rem', color: TEXT_MUTED, marginTop: '0.25rem' }}>
                Select all events where this dish is served. Leave empty if not tied to a specific event.
              </div>
            </div>
            <div style={styles.formActions}>
              <button type="submit" style={styles.btnPrimary} aria-label="Save menu item">
                {editingItem ? 'Update' : 'Create'}
              </button>
              <button
                type="button"
                style={styles.btnSecondary}
                onClick={resetForm}
                aria-label="Cancel"
              >
                Cancel
              </button>
            </div>
          </form>
          </div>
        </div>
      )}

      <div style={{ ...styles.card, overflowX: 'auto' }}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Name</th>
              <th style={styles.th}>Category</th>
              <th style={styles.th}>Events</th>
              <th style={styles.th}>Tags</th>
              <th style={styles.th}>Active</th>
              <th style={styles.th}># Vendors</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td style={styles.td} colSpan={7}>
                  <em style={{ color: TEXT_MUTED }}>No menu items yet. Add one to get started.</em>
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id}>
                  <td style={styles.td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                      {item.imageUrl && (
                        <img
                          src={item.imageUrl.startsWith('http') ? item.imageUrl : `${API_BASE}${item.imageUrl}`}
                          alt={item.name}
                          style={{ width: '40px', height: '40px', borderRadius: '8px', objectFit: 'cover', flexShrink: 0, backgroundColor: '#f3f4f6' }}
                        />
                      )}
                      <div>
                        <strong>{item.name}</strong>
                        {item.description && (
                          <div style={{ fontSize: '0.8rem', color: TEXT_MUTED, marginTop: '2px' }}>
                            {item.description.length > 60
                              ? item.description.slice(0, 60) + '...'
                              : item.description}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td style={styles.td}>{item.category || '—'}</td>
                  <td style={styles.td}>
                    {item.eventLinks && item.eventLinks.length > 0 ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {item.eventLinks.map((link) => (
                          <span
                            key={link.id}
                            style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '0.72rem', fontWeight: 600, backgroundColor: '#e8f5e9', color: PRIMARY }}
                          >
                            {link.event?.name || 'Event'}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span style={{ color: TEXT_MUTED }}>—</span>
                    )}
                  </td>
                  <td style={styles.td}>
                    {item.tags || '—'}
                  </td>
                  <td style={styles.td}>
                    <button
                      style={{
                        ...styles.badge,
                        ...(item.isActive ? styles.badgeActive : styles.badgeInactive),
                        cursor: 'pointer',
                        border: 'none',
                      }}
                      onClick={() => handleToggleActive(item)}
                      aria-label={`Toggle ${item.name} ${item.isActive ? 'inactive' : 'active'}`}
                    >
                      {item.isActive ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td style={styles.td}>
                    {item.vendorOfferings?.length || 0}
                  </td>
                  <td style={styles.td}>
                    <button
                      style={styles.iconBtn}
                      onClick={() => openEditForm(item)}
                      title="Edit"
                      aria-label={`Edit ${item.name}`}
                    >
                      ✏️
                    </button>
                    <button
                      style={styles.iconBtn}
                      onClick={() => handleDelete(item)}
                      title="Delete"
                      aria-label={`Delete ${item.name}`}
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============ VENDOR OFFERINGS TAB ============
function VendorOfferingsTab() {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [offerings, setOfferings] = useState<VendorOffering[]>([]);
  const [selectedVendorId, setSelectedVendorId] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingVendors, setLoadingVendors] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingOffering, setEditingOffering] = useState<VendorOffering | null>(null);
  const [formMenuItemId, setFormMenuItemId] = useState('');
  const [formPrice, setFormPrice] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const [formAvailable, setFormAvailable] = useState(true);
  const [vendorCounts, setVendorCounts] = useState<Record<string, number>>({});

  const fetchVendors = useCallback(async () => { try { setLoadingVendors(true); const data = await apiClient<Vendor[]>('/vendors'); setVendors(data); } catch (err: any) { setError(err.message || 'Failed to load vendors'); } finally { setLoadingVendors(false); } }, []);
  const fetchMenuItems = useCallback(async () => { try { const data = await apiClient<MenuItem[]>('/food-catalog/menu-items'); setMenuItems(data); } catch {} }, []);
  const fetchOfferings = useCallback(async (vendorId: string) => { if (!vendorId) { setOfferings([]); return; } setLoading(true); setError(''); try { const data = await apiClient<VendorOffering[]>(`/food-catalog/offerings/vendor/${vendorId}`); const items = Array.isArray(data) ? data : []; setOfferings(items); setVendorCounts((prev) => ({ ...prev, [vendorId]: items.length })); } catch (err: any) { setError(err?.message || 'Failed to load offerings.'); setOfferings([]); } setLoading(false); }, []);

  useEffect(() => { fetchVendors(); fetchMenuItems(); }, [fetchVendors, fetchMenuItems]);
  useEffect(() => { if (selectedVendorId) fetchOfferings(selectedVendorId); else setOfferings([]); }, [selectedVendorId, fetchOfferings]);

  const resetForm = () => { setFormMenuItemId(''); setFormPrice(''); setFormNotes(''); setFormAvailable(true); setEditingOffering(null); setShowForm(false); };
  const openEditForm = (offering: VendorOffering) => { setEditingOffering(offering); setFormMenuItemId(offering.foodMenuItemId); setFormPrice(offering.price != null ? String(Number(offering.price)) : ''); setFormNotes(offering.notes || ''); setFormAvailable(offering.available); setShowForm(true); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError('');
      if (editingOffering) {
        await apiClient(`/food-catalog/offerings/${editingOffering.id}`, { method: 'PATCH', body: { price: formPrice ? Number(formPrice) : undefined, available: formAvailable, notes: formNotes || undefined } });
      } else {
        if (!formMenuItemId || !selectedVendorId) return;
        await apiClient('/food-catalog/offerings', { method: 'POST', body: { vendorId: selectedVendorId, foodMenuItemId: formMenuItemId, price: formPrice ? Number(formPrice) : undefined, available: formAvailable, notes: formNotes || undefined } });
      }
      resetForm();
      await fetchOfferings(selectedVendorId);
    } catch (err: any) { setError(err.message || 'Failed to save offering'); }
  };

  const handleDelete = async (offering: VendorOffering) => { if (!window.confirm(`Remove "${offering.foodMenuItem?.name || 'this offering'}"?`)) return; try { setError(''); await apiClient(`/food-catalog/offerings/${offering.id}`, { method: 'DELETE' }); await fetchOfferings(selectedVendorId); } catch (err: any) { setError(err.message || 'Failed to delete'); } };
  const handleToggleAvailable = async (offering: VendorOffering) => { try { setError(''); await apiClient(`/food-catalog/offerings/${offering.id}`, { method: 'PATCH', body: { available: !offering.available } }); await fetchOfferings(selectedVendorId); } catch (err: any) { setError(err.message || 'Failed to update'); } };

  if (loadingVendors) return <div style={styles.loading}>Loading vendors...</div>;
  const selectedVendor = vendors.find((v) => v.id === selectedVendorId);

  return (
    <div style={{ display: 'flex', gap: '1rem', minHeight: '500px' }}>
      {/* LEFT PANE — Vendor list */}
      <div style={{ width: '260px', flexShrink: 0, backgroundColor: BG_CARD, border: `1px solid ${BORDER_LIGHT}`, borderRadius: '8px', overflowY: 'auto', maxHeight: '70vh' }}>
        <div style={{ padding: '0.75rem 1rem', borderBottom: `1px solid ${BORDER_LIGHT}`, fontSize: '0.72rem', fontWeight: 600, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Vendors ({vendors.length})
        </div>
        {vendors.map((v) => (
          <div
            key={v.id}
            onClick={() => { setSelectedVendorId(v.id); resetForm(); }}
            style={{
              padding: '0.6rem 1rem',
              cursor: 'pointer',
              fontSize: '0.85rem',
              fontWeight: selectedVendorId === v.id ? 600 : 400,
              color: selectedVendorId === v.id ? PRIMARY : TEXT_DARK,
              backgroundColor: selectedVendorId === v.id ? '#e8f5e9' : 'transparent',
              borderLeft: selectedVendorId === v.id ? `3px solid ${PRIMARY}` : '3px solid transparent',
              transition: 'all 0.15s',
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && setSelectedVendorId(v.id)}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
              <span>{v.businessName}{vendorCounts[v.id] != null ? ` (${vendorCounts[v.id]})` : ''}</span>
              {v.category && (
                <span style={{ display: 'inline-block', padding: '1px 7px', borderRadius: '10px', fontSize: '0.68rem', fontWeight: 600, backgroundColor: '#eef2ff', color: '#4338ca' }}>
                  {v.category}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* RIGHT PANE — Offerings */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {error && <div style={styles.error}>{error}</div>}

        {!selectedVendorId ? (
          <div style={{ ...styles.card, textAlign: 'center', padding: '3rem', color: TEXT_MUTED }}>
            <p style={{ fontSize: '1.5rem', margin: '0 0 0.5rem' }}>👈</p>
            <p style={{ margin: 0 }}>Select a vendor to manage their food offerings.</p>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.1rem', color: TEXT_DARK }}>{selectedVendor?.businessName}</h3>
                <span style={{ fontSize: '0.8rem', color: TEXT_MUTED }}>{offerings.length} offering{offerings.length !== 1 ? 's' : ''}</span>
              </div>
              <button style={styles.btnPrimary} onClick={() => setShowForm(true)} aria-label="Assign dish">+ Assign Dish</button>
            </div>

            {/* Assign modal */}
            {showForm && (
              <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }} onClick={(e) => e.target === e.currentTarget && resetForm()} role="dialog" aria-modal="true">
                <div style={{ backgroundColor: '#fff', borderRadius: '16px', width: '100%', maxWidth: '450px', padding: '1.5rem', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', position: 'relative' }}>
                  <button onClick={resetForm} style={{ position: 'absolute', top: '12px', right: '12px', background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: TEXT_MUTED }} aria-label="Close">✕</button>
                  <h3 style={{ margin: '0 0 1rem', fontSize: '1.1rem', color: TEXT_DARK }}>{editingOffering ? '✏️ Edit Offering' : `➕ Assign Dish to ${selectedVendor?.businessName}`}</h3>
                  <form onSubmit={handleSubmit}>
                    {!editingOffering && (
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Menu Item *</label>
                        <select style={styles.select} value={formMenuItemId} onChange={(e) => setFormMenuItemId(e.target.value)} required>
                          <option value="">— Select a dish —</option>
                          {menuItems.map((item) => <option key={item.id} value={item.id}>{item.name}{item.category ? ` (${item.category})` : ''}</option>)}
                        </select>
                      </div>
                    )}
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Price (KDU$)</label>
                      <input style={styles.input} type="number" step="0.01" min="0" value={formPrice} onChange={(e) => setFormPrice(e.target.value)} placeholder="Leave empty if not yet determined" />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Notes</label>
                      <input style={styles.input} value={formNotes} onChange={(e) => setFormNotes(e.target.value)} placeholder="e.g., extra spicy version" />
                    </div>
                    <div style={{ ...styles.formGroup, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input type="checkbox" checked={formAvailable} onChange={(e) => setFormAvailable(e.target.checked)} />
                      <label style={{ fontSize: '0.9rem', color: TEXT_DARK }}>Available</label>
                    </div>
                    <div style={styles.formActions}>
                      <button type="submit" style={styles.btnPrimary}>{editingOffering ? 'Update' : 'Assign'}</button>
                      <button type="button" style={styles.btnSecondary} onClick={resetForm}>Cancel</button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Offerings table */}
            {loading ? <div style={styles.loading}>Loading...</div> : (
              <div style={{ ...styles.card, overflowX: 'auto' }}>
                <table style={styles.table}>
                  <thead><tr><th style={styles.th}>Dish</th><th style={styles.th}>Price</th><th style={styles.th}>Available</th><th style={styles.th}>Notes</th><th style={styles.th}>Actions</th></tr></thead>
                  <tbody>
                    {offerings.length === 0 ? (
                      <tr><td style={styles.td} colSpan={5}><em style={{ color: TEXT_MUTED }}>No dishes assigned. Click "+ Assign Dish" to add one.</em></td></tr>
                    ) : offerings.map((o) => (
                      <tr key={o.id}>
                        <td style={styles.td}><strong>{o.foodMenuItem?.name || '—'}</strong></td>
                        <td style={styles.td}>{o.price != null && Number(o.price) > 0 ? `${Number(o.price).toFixed(2)} KDU$` : <span style={{ color: TEXT_MUTED, fontStyle: 'italic' }}>Not set</span>}</td>
                        <td style={styles.td}><button style={{ ...styles.badge, ...(o.available ? styles.badgeActive : styles.badgeInactive), cursor: 'pointer', border: 'none' }} onClick={() => handleToggleAvailable(o)}>{o.available ? 'Yes' : 'No'}</button></td>
                        <td style={styles.td}>{o.notes || '—'}</td>
                        <td style={styles.td}><button style={styles.iconBtn} onClick={() => openEditForm(o)} title="Edit">✏️</button><button style={styles.iconBtn} onClick={() => handleDelete(o)} title="Delete">🗑️</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ============ MAIN PAGE ============
function FoodCatalogManagePageInner() {
  const [activeView, setActiveView] = useState<SubView>('menu-items');

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#0f2d1f', margin: '0 0 0.25rem' }}>
          🍽️ Food Catalog Management
        </h1>
        <p style={styles.subtitle}>
          Manage menu items and vendor offerings for the Awujoh food festival
        </p>
      </div>

      <nav style={styles.subNav} aria-label="Food catalog views">
        {SUB_VIEWS.map((view) => (
          <button
            key={view.key}
            style={activeView === view.key ? styles.subTabActive : styles.subTab}
            onClick={() => setActiveView(view.key)}
            aria-current={activeView === view.key ? 'page' : undefined}
          >
            {view.label}
          </button>
        ))}
      </nav>

      {activeView === 'menu-items' && <MenuItemsTab />}
      {activeView === 'offerings' && <VendorOfferingsTab />}
    </div>
  );
}

// Error boundary to prevent blank screen on unexpected errors
class FoodCatalogErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean; error: string }> {
  state = { hasError: false, error: '' };
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error: error.message };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#d32f2f' }}>
          <p style={{ fontSize: '1.2rem' }}>Something went wrong loading this page.</p>
          <p style={{ fontSize: '0.85rem', color: '#666' }}>{this.state.error}</p>
          <button
            onClick={() => { this.setState({ hasError: false, error: '' }); window.location.reload(); }}
            style={{ marginTop: '1rem', padding: '0.5rem 1rem', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
          >
            Reload Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export function FoodCatalogManagePage() {
  return (
    <FoodCatalogErrorBoundary>
      <FoodCatalogManagePageInner />
    </FoodCatalogErrorBoundary>
  );
}
